import { forwardRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { birthdayLater, getMyBirthday, setMyBirthday, staffBirthdaysToday } from "@/lib/api";

// Staff birthdays (backend: routes/staff_birthdays.py). The year is optional and only managers and admins
// ever see it; colleagues just get the first name on the day.
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
  "October", "November", "December"];
const DAYS_IN = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function toDob({ day, month, year }) {
  if (!day || !month) return "";
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return year ? `${year}-${mm}-${dd}` : `--${mm}-${dd}`;
}

// "14/8", "14/08/1995", "14-8-1995" -> "1995-08-14" / "--08-14"; "" clears; null when it can't be read.
export function parseTypedBirthday(text) {
  const s = (text || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{4}))?$/);
  if (!m) return null;
  return toDob({ day: +m[1], month: +m[2], year: m[3] ? +m[3] : null });
}

export function birthdayText(b) {
  if (!b) return "";
  return `${b.day} ${MONTHS[b.month - 1]}${b.year ? ` ${b.year}` : ""}`;
}

const selectCls = "h-9 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm text-zinc-100";

// Day + month (+ optional year) with a Save button.
export const BirthdayField = forwardRef(function BirthdayField({ value, onSaved, extra }, ref) {
  const [day, setDay] = useState(value?.day || "");
  const [month, setMonth] = useState(value?.month || "");
  const [year, setYear] = useState(value?.year || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDay(value?.day || ""); setMonth(value?.month || ""); setYear(value?.year || ""); }, [value]);
  const maxDay = month ? DAYS_IN[month - 1] : 31;

  const save = async () => {
    if (!day || !month) return toast.error("Pick the day and the month");
    if (year && !/^\d{4}$/.test(String(year))) return toast.error("The year needs 4 digits, or leave it empty");
    setBusy(true);
    try {
      const r = await setMyBirthday(toDob({ day, month, year }));
      toast.success("Birthday saved. Thank you!");
      onSaved?.(r.birthday);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your birthday");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="birthday-field">
      <label className="text-xs text-zinc-400 flex flex-col gap-1">Day
        <select ref={ref} value={day} onChange={(e) => setDay(+e.target.value || "")} className={selectCls} data-testid="birthday-day">
          <option value="">Day</option>
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>
      <label className="text-xs text-zinc-400 flex flex-col gap-1">Month
        <select value={month} onChange={(e) => { const m = +e.target.value || ""; setMonth(m); if (m && day > DAYS_IN[m - 1]) setDay(""); }}
          className={selectCls} data-testid="birthday-month">
          <option value="">Month</option>
          {MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
        </select>
      </label>
      <label className="text-xs text-zinc-400 flex flex-col gap-1">Year (optional)
        <input inputMode="numeric" maxLength={4} value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, ""))}
          placeholder="1995" className={`${selectCls} w-20`} data-testid="birthday-year" />
      </label>
      <Button type="button" size="sm" onClick={save} disabled={busy} className="h-9" data-testid="birthday-save">Save</Button>
      {extra}
    </div>
  );
});

// After sign-in: anyone without a birthday saved is asked once; "Not now" hides it for 7 days.
export function BirthdayPrompt() {
  const loc = useLocation();
  const [show, setShow] = useState(false);
  useEffect(() => { getMyBirthday().then((d) => setShow(!!d.prompt)).catch(() => {}); }, []);
  if (!show || loc.pathname === "/profile") return null;
  const later = () => { setShow(false); birthdayLater().catch(() => {}); };
  return (
    <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/5 px-4 py-3" data-testid="birthday-prompt">
      <div className="text-sm font-medium text-zinc-100">🎂 Add your birthday so the team can celebrate it</div>
      <div className="text-xs text-zinc-400 mt-0.5 mb-3">Your colleagues only see the day, never the year.</div>
      <BirthdayField onSaved={() => setShow(false)}
        extra={<Button type="button" size="sm" variant="ghost" onClick={later} className="h-9 text-zinc-400" data-testid="birthday-later">Not now</Button>} />
    </div>
  );
}

function joinNames(names) {
  if (names.length <= 1) return names[0] || "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// Dashboard: "It's Sana's birthday today" for the gym; the birthday person gets "Happy birthday".
export function BirthdayBanner({ facilityId }) {
  const [people, setPeople] = useState([]);
  useEffect(() => { staffBirthdaysToday().then((d) => setPeople(d.people || [])).catch(() => setPeople([])); }, [facilityId]);
  if (!people.length) return null;
  const me = people.find((p) => p.is_me);
  const others = people.filter((p) => !p.is_me).map((p) => p.first_name);
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 space-y-1" data-testid="birthday-banner">
      {me && <div>Happy birthday, {me.first_name}! 🎂</div>}
      {others.length > 0 && (
        <div>🎂 It's {joinNames(others)}'s birthday today{me ? " too" : ""}</div>
      )}
    </div>
  );
}
